// The scan screen for one order going out. Scans (the handheld's keyboard wedge, or a typed
// code and Enter) build a list of serials; "N of M" says how far along it is, and when more
// than one drone line is owed, what each still needs. A count field for each non-drone line
// still owed sits under the list. One submit records the lot. A refusal marks every serial it names and keeps the list so the operator removes that
// one and carries on; a warning offers Record anyway, which re-sends with confirm: true.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../../apiHelpers';
import { BTN_TONAL, FOCUS, MOTION } from '../../Requisitions/buttonStyles';
import { shipError, shipErrorMessage, shipWarnings, warningDetail } from '../../Sales/shipping';
import type { ShipBody, Shipping, ShipResult, ShipWarning } from '../../Sales/shipping';
import useScannerInput from '../hooks/useScannerInput';
import { CheckIcon, WarningIcon } from '../icons';
import { buzz } from '../shell';
import { FIELD, focusForScan, Notice, ScanField } from '../ui';
import type { QueueOrder, ScanDirty } from './types';

interface Props {
  order: QueueOrder;
  /** The badged operator; sent as operator_id only on a shared device. */
  operatorId?: string;
  isSharedMode?: boolean;
  onRecorded: (result: ShipResult, total: number) => void;
  /** Reports whether leaving now would lose work: serials on the list, or counts changed from their prefill. */
  onDirty?: (state: ScanDirty) => void;
  /** True while the discard confirm is open: a hardware scan then adds nothing. */
  paused?: boolean;
}

// Module scope so the hook's listeners keep a stable identity. The server decides what a
// serial is; this only drops what cannot be a code at all.
const isCode = (val: string) => /^\S{4,}$/.test(val);

const digits = (v: string) => v.replace(/[^0-9]/g, '');

const ShipScan: React.FC<Props> = ({ order, operatorId, isSharedMode = false, onRecorded, onDirty, paused = false }) => {
  const api = useApi();
  const scanRef = useRef<HTMLInputElement>(null);

  const [shipping, setShipping] = useState<Shipping | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [serials, setSerials] = useState<string[]>([]);
  const [refused, setRefused] = useState<string[]>([]);
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [tracking, setTracking] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<ShipWarning[] | null>(null);
  const [busy, setBusy] = useState(false);
  const prefill = useRef<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      const s = (await api.get(`/orders/${order.id}/shipping`)) as Shipping;
      setShipping(s);
      prefill.current = Object.fromEntries(s.lines.filter((l) => !l.serialized && l.remaining > 0).map((l) => [l.sales_order_line_id, String(l.remaining)]));
      setCounts(prefill.current);
    } catch {
      setLoadFailed(true);
    }
  }, [order.id]);

  useEffect(() => {
    load();
  }, [load]);

  // What each line can still take is what the order still owes on it.
  const { countRows, droneLines, expected } = useMemo(() => {
    const open = (shipping?.lines ?? []).filter((l) => l.remaining > 0);
    const serialized = open.filter((l) => l.serialized);
    return {
      countRows: open.filter((l) => !l.serialized).map((l) => ({ id: l.sales_order_line_id, label: l.label, max: l.remaining })),
      droneLines: serialized,
      expected: shipping ? serialized.reduce((n, l) => n + l.remaining, 0) : order.expected,
    };
  }, [shipping, order.expected]);

  const countOf = (id: string) => Number(counts[id] || 0);
  const countTotal = countRows.reduce((n, r) => n + countOf(r.id), 0);
  const total = serials.length + countTotal;
  const countsChanged = Object.keys({ ...prefill.current, ...counts }).some((id) => (counts[id] ?? '') !== (prefill.current[id] ?? ''));
  const dirty = serials.length > 0 || countsChanged;
  useEffect(() => {
    onDirty?.({ serials: serials.length, dirty });
  }, [serials.length, dirty]);
  useEffect(() => () => onDirty?.({ serials: 0, dirty: false }), []);
  const showScan = !shipping || expected > 0;

  const touched = () => {
    setError(null);
    setWarnings(null);
  };

  // The list is read from a ref so a duplicate check never lives inside a state updater.
  const listRef = useRef<string[]>([]);
  const setList = useCallback((next: string[]) => {
    listRef.current = next;
    setSerials(next);
  }, []);

  const add = useCallback((raw: string) => {
    const s = raw.trim();
    if (!s) return;
    if (listRef.current.some((x) => x.toLowerCase() === s.toLowerCase())) {
      setNote(`${s} is already on the list.`);
      return;
    }
    setNote(null);
    setList([s, ...listRef.current]);
    setError(null);
    setWarnings(null);
  }, [setList]);

  const remove = (s: string) => {
    setList(listRef.current.filter((x) => x !== s));
    setRefused((prev) => prev.filter((r) => r !== s.toLowerCase()));
    touched();
    focusForScan(scanRef);
  };

  useScannerInput({
    onScan: add,
    surface: 'ship',
    minLength: 4,
    validator: isCode,
    disabled: busy || !!warnings || loadFailed || paused,
  });

  const send = async (confirm: boolean) => {
    const over = countRows.find((r) => countOf(r.id) > r.max);
    if (over) {
      buzz('reject');
      setError(`${over.label}: only ${over.max} still owed on this order.`);
      return;
    }
    const body: ShipBody = {
      direction: 'out',
      tracking_number: tracking.trim() || undefined,
      serials: [...serials].reverse(),
      counts: countRows.filter((r) => countOf(r.id) > 0).map((r) => ({ sales_order_line_id: r.id, quantity: countOf(r.id) })),
      ...(confirm ? { confirm: true } : {}),
      ...(isSharedMode && operatorId ? { operator_id: operatorId } : {}),
    };
    setBusy(true);
    setError(null);
    try {
      const result = (await api.post(`/orders/${order.id}/shipments`, { body })) as ShipResult;
      onRecorded(result, total);
    } catch (err) {
      const found = shipWarnings(err);
      if (found) {
        buzz('warn');
        setWarnings(found);
      } else {
        buzz('reject');
        setWarnings(null);
        const e = shipError(err);
        const named = [...(Array.isArray(e.serials) ? e.serials : []), ...(e.serial ? [e.serial] : [])];
        setRefused(named.map((n) => String(n).toLowerCase()));
        setError(shipErrorMessage(err));
      }
      focusForScan(scanRef);
    } finally {
      setBusy(false);
    }
  };

  if (loadFailed) {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col items-start gap-3 px-4 pt-2">
        <Notice tone="miss">This order did not load. Check the connection and try again.</Notice>
        <button type="button" onClick={load} className={`${BTN_TONAL} min-h-[2.75rem] px-5`}>Try again</button>
      </div>
    );
  }

  const complete = expected > 0 && serials.length === expected;
  const over = expected > 0 && serials.length > expected;

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col">
      <div className="shrink-0 px-4 pb-3">
        {showScan && (
          <>
            <div className="mb-3">
              <p className="flex items-baseline justify-center gap-2 text-center" aria-live="polite">
                {complete && <CheckIcon className="h-6 w-6 shrink-0 self-center text-primary" />}
                {over && <WarningIcon className="h-6 w-6 shrink-0 self-center text-amber-200" />}
                <span className={`font-mono text-3xl font-semibold tabular-nums ${complete ? 'text-primary' : over ? 'text-amber-200' : 'text-on-surface'}`}>
                  {serials.length} of {expected}
                </span>
                <span className="text-base text-on-surface-variant">scanned</span>
              </p>
              {droneLines.length > 1 && (
                <p className="mt-1 text-center text-sm tabular-nums text-on-surface-variant">
                  {droneLines.map((l) => `${l.remaining} ${l.label}`).join(', ')}
                </p>
              )}
              {over && (
                <p className="mt-1 text-center text-sm text-amber-200">
                  {serials.length - expected} more than this order needs
                </p>
              )}
              <div className="mx-auto mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface-container-highest">
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-200 ease-emphasized motion-reduce:transition-none"
                  style={{ width: `${expected > 0 ? Math.min(100, (serials.length / expected) * 100) : 0}%` }}
                />
              </div>
            </div>
            <ScanField inputRef={scanRef} label="Scan each drone going out" busy={busy} onSubmit={add} />
            <p role="status" className="mt-1 min-h-[1.25rem] text-center text-sm text-on-surface-variant">{note ?? ''}</p>
          </>
        )}
      </div>

      <form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          if (total > 0 && !busy && !warnings) send(false);
        }}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 pb-3">
          {error && (
            <div role="alert">
              <Notice tone="miss">{error}</Notice>
            </div>
          )}

          {serials.length > 0 && (
            <ul aria-label="Serials to record" className="flex flex-col gap-1.5">
              {serials.map((s) => {
                const bad = refused.includes(s.toLowerCase());
                return (
                  <li
                    key={s}
                    className={`flex min-h-[2.75rem] items-center gap-2 rounded-lg py-0.5 pl-3 pr-1 motion-safe:animate-rowLand
                      ${bad ? 'bg-red-900/40 text-red-100' : 'bg-surface-container text-on-surface'}`}
                  >
                    {bad && <WarningIcon className="h-5 w-5 shrink-0" />}
                    {/* The serial is what the operator matches against the drone in hand, so it keeps the
                        full width; Refused sits under it rather than beside it. */}
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate font-mono text-base">{s}</span>
                      {bad && <span className="text-xs font-medium">Refused</span>}
                    </span>
                    <button
                      type="button"
                      onClick={() => remove(s)}
                      aria-label={`Remove ${s}`}
                      title={`Take ${s} off the list`}
                      className={`inline-flex h-11 shrink-0 items-center rounded-full px-4 text-sm font-medium text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface ${FOCUS} ${MOTION}`}
                    >
                      Remove
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {countRows.length > 0 && (
            <section aria-label="Items shipped by count" className="rounded-xl bg-surface-container-low p-3">
              <h2 className="mb-2 text-sm font-semibold text-on-surface-variant">Also on this order</h2>
              <div className="flex flex-col gap-2">
                {countRows.map((r) => (
                  <label key={r.id} className="flex items-center gap-3">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-base text-on-surface" title={r.label}>{r.label}</span>
                      <span className="block text-sm tabular-nums text-on-surface-variant">{r.max} still owed</span>
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="off"
                      aria-label={`${r.label} count`}
                      value={counts[r.id] ?? ''}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => {
                        setCounts((prev) => ({ ...prev, [r.id]: digits(e.target.value) }));
                        touched();
                      }}
                      className={`${FIELD} !w-24 shrink-0 text-center font-mono tabular-nums`}
                    />
                  </label>
                ))}
              </div>
            </section>
          )}

          <label className="block">
            <span className="mb-1 block text-sm text-on-surface-variant">Tracking number, if you have it</span>
            <input
              type="text"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              className={`${FIELD} font-mono`}
            />
          </label>

          {warnings && (
            <div role="alert" className="flex flex-col gap-2 rounded-xl bg-red-900/30 p-3">
              <p className="flex items-center gap-2 text-base font-semibold text-red-100">
                <WarningIcon className="h-5 w-5 shrink-0" />
                The record disagrees with {warnings.length === 1 ? 'this drone' : 'these drones'}
              </p>
              <ul className="flex flex-col gap-1 text-base text-on-surface">
                {warnings.map((w) => (
                  <li key={`${w.serial}-${w.code}`} className="break-words">
                    <span className="font-mono">{w.serial}</span> {warningDetail(w)}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-on-surface-variant">
                Recording anyway saves them marked for reconciling and notes your override on each drone.
              </p>
            </div>
          )}
        </div>

        <div className="shrink-0 bg-surface px-4 pb-4 pt-2">
          {warnings ? (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => send(true)}
                disabled={busy}
                title="Save this anyway and note the override on each drone"
                className={`min-h-[3.25rem] w-full rounded-full bg-red-900/60 px-5 text-base font-semibold text-red-100 hover:bg-red-900/80 disabled:opacity-40 ${FOCUS} ${MOTION}`}
              >
                {busy ? 'Recording…' : 'Record anyway'}
              </button>
              <button type="button" onClick={() => setWarnings(null)} disabled={busy} className={`${BTN_TONAL} min-h-[2.75rem] w-full justify-center`}>
                Back to the list
              </button>
            </div>
          ) : (
            <button
              type="submit"
              disabled={total === 0 || busy}
              className={`min-h-[3.25rem] w-full rounded-full bg-primary px-5 text-base font-bold text-on-primary np-state np-state-on-primary disabled:opacity-40 ${FOCUS} ${MOTION}`}
            >
              {busy ? 'Recording…' : total > 0 ? `Ship ${total}` : 'Ship'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ShipScan;
